import { useState, useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { cloneDeep } from "lodash";
import styles from './SchoolCoursesCustomize.module.scss';
import CustomizeUnits from 'src/components/customize-units/CustomizeUnits';
import {
  SchoolDataFragment,
  UnitContentFragment,
  useGetCourseQuery,
  useGetAusCourseQuery,
  useCustomizeCoursesForSchoolMutation,
} from '@api/graphql-codegen'
import Spinner from '@src/components/spinner/Spinner';
import { useToast } from '@src/hooks/useToast';

type SchoolCourse = {
  id: string
  newId?: string | null
  customized: boolean
  isCustomizing: boolean
  isAusCourse: boolean
}

type CustomizedSchoolCourse = {
  programId: string
  primary: string[]
  additional: string[]
  courseContents: Array<{id: string; type: any; position: number}>
}

function SchoolCoursesCustomize() {
  const { state: { schoolData, schoolCourses } } = useLocation<{ schoolData: SchoolDataFragment, schoolCourses: Array<SchoolCourse> }>();
  const [courseIndex, setCourseIndex] = useState(0);
  const [customizedSchoolCourses, setCustomizedSchoolCourses] = useState<Array<CustomizedSchoolCourse>>([])
  const history = useHistory();
  const { successToast } = useToast();

  const customizingCourses = useMemo(() => schoolCourses.filter(sc => !!sc.isCustomizing), [schoolCourses])

  const currentCourse = useMemo(() => {
    if (courseIndex > customizingCourses.length) {
      return undefined
    }
    return customizingCourses[courseIndex]
  }, [courseIndex, customizingCourses])

  const hasNext = useMemo(() => courseIndex < schoolCourses.length, [courseIndex, schoolCourses.length])

  const {data: programData, loading: programDataLoading} = useGetCourseQuery({
    variables: {
      input: { id: currentCourse?.id as string }
    },
    fetchPolicy: 'network-only', skip: !currentCourse || currentCourse.isAusCourse
  })
  const {data: ausProgramData, loading: ausProgramDataLoading} = useGetAusCourseQuery({
    variables: { input: { id: (currentCourse?.newId || currentCourse?.id) as string } }, fetchPolicy: 'network-only', skip: !currentCourse || !currentCourse.isAusCourse })

  const [customizeCoursesForSchool, { loading: isSchoolCourseUpdating }] = useCustomizeCoursesForSchoolMutation();

  const handleOpenNew = useCallback(async (primary: UnitContentFragment[], additional: UnitContentFragment[], primaryOPReelContents?: any[]) => {
    if (!currentCourse) return
    const _customizedSchoolCourses = cloneDeep(customizedSchoolCourses)
    _customizedSchoolCourses.push({
      programId: currentCourse.id,
      primary: primary.map(unit => unit.id),
      additional: additional.map(unit => unit.id),
      courseContents: (primaryOPReelContents || []).map((content, index) => ({
        id: content.id,
        type: content.type,
        position: index + 1
      }))
    })
    setCustomizedSchoolCourses(_customizedSchoolCourses)

    if ((customizingCourses.length - 1) >= courseIndex + 1) {
      setCourseIndex(courseIndex + 1)
    } else {
      const { data } = await customizeCoursesForSchool({
        variables: {
          input: {
            schoolId: schoolData.id,
            defaultPrograms: schoolCourses.map(sc => ({ id: sc.id, newId: sc.newId, isCustomizing: sc.isCustomizing })),
            customizedPrograms: _customizedSchoolCourses
          }
        }
      })
      if (data?.customizeCoursesForSchool) {
        successToast(`${schoolData.name} is successfully updated!`);
        history.go(-2)
      }
    }
  }, [courseIndex, currentCourse, customizeCoursesForSchool, customizedSchoolCourses, customizingCourses.length, history, schoolCourses, schoolData.id, schoolData.name, successToast]);

  const handleCancel = () => {
    history.goBack();
  };

  const course = useMemo(() => {
    if (programData) {
      const { getCourse, getProgramUnits } = programData
      return { ...getCourse, ...getProgramUnits }
    }
    if (ausProgramData) {
      return ausProgramData.getAUSCourse
    }
    return undefined
  }, [programData, ausProgramData])

  return (
    <>
      <div className={styles.unitView}>
        {(programDataLoading || ausProgramDataLoading) ? (
          <div className={styles.spinner}><Spinner /></div>
        ) : (
          <>
            {course && (
              <CustomizeUnits
                type="school"
                course={course}
                school={schoolData}
                hasNext={hasNext}
                isDisabled={isSchoolCourseUpdating}
                handleOpenNew={handleOpenNew}
                handleCancel={handleCancel}
                isSchoolCourse
              />
            )}
          </>
        )}
      </div>
    </>
  );
}

export default SchoolCoursesCustomize;
